import { useState, useEffect, useCallback, useRef } from "react";
import { Link } from "react-router-dom";
import { useAuth } from "@/contexts/AuthContext";
import { systemApi, adminApi, notificationsApi } from "@/lib/api";
import { ViewToggle } from "@/components/ViewToggle";
import { Sidebar } from "@/components/Sidebar";
import { toast } from "sonner";
import { Gear, EnvelopeSimple, CurrencyDollar, ChatText, WhatsappLogo, Palette, Users, SignOut, ShieldCheck, ArrowLeft, Lifebuoy, Copy, ArrowsClockwise, Archive, DownloadSimple, Bell, Trash, Bank } from "@phosphor-icons/react";

const TABS = [
  { key: "email",      label: "Email / SMTP",   icon: EnvelopeSimple },
  { key: "mpesa",      label: "M-Pesa Daraja",  icon: CurrencyDollar },
  { key: "lks",        label: "LKS (alt. rail)", icon: Bank },
  { key: "sms",        label: "SMS",            icon: ChatText },
  { key: "whatsapp",   label: "WhatsApp",       icon: WhatsappLogo },
  { key: "branding",   label: "Branding & UI",  icon: Palette },
  { key: "admins",     label: "Admin Users",    icon: Users },
  { key: "loginsupport", label: "Login Support", icon: Lifebuoy },
  { key: "notifications", label: "Notifications report", icon: Bell },
  { key: "export",     label: "Export Bundle",  icon: Archive },
];

export default function SystemAdmin() {
  const { user, logout } = useAuth();
  const [tab, setTab] = useState("email");
  const [config, setConfig] = useState(null);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [testEmailTo, setTestEmailTo] = useState("");
  const [testSmsTo, setTestSmsTo] = useState("");
  const [testWaTo, setTestWaTo] = useState("");
  const [admins, setAdmins] = useState([]);

  const loadConfig = useCallback(async () => {
    try {
      const { data } = await systemApi.getConfig();
      setConfig(data);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Failed to load system config");
    } finally {
      setLoading(false);
    }
  }, []);

  const loadAdmins = useCallback(async () => {
    try {
      const { data } = await systemApi.listAdminUsers();
      setAdmins(data);
    } catch {}
  }, []);

  useEffect(() => { loadConfig(); loadAdmins(); }, [loadConfig, loadAdmins]);

  const updateField = (section, key, value) => {
    setConfig((c) => ({ ...c, [section]: { ...(c?.[section] || {}), [key]: value } }));
  };

  const handleSaveSection = async (section) => {
    if (!config) return;
    setSaving(true);
    try {
      const { data } = await systemApi.saveSection(section, config[section]);
      setConfig((c) => ({ ...c, ...data }));
      toast.success(`${TABS.find(t => t.key === section)?.label || section} saved`);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Save failed");
    } finally {
      setSaving(false);
    }
  };

  const handleTestEmail = async () => {
    try {
      const { data } = await adminApi.emailTest(testEmailTo);
      toast.success(`Test email sent to ${data.to}`);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Email test failed");
    }
  };

  const handleTestMpesa = async () => {
    try {
      const { data } = await systemApi.testMpesa();
      const msg = data.message || "M-Pesa config OK";
      if (data.ready === false && data.warnings?.length) {
        toast.warning(msg + " " + data.warnings.join(" "), { duration: 8000 });
      } else if (data.ok && data.tokenPreview) {
        toast.success(`${msg} (${data.environment} · token ${data.tokenPreview})`, { duration: 6000 });
      } else {
        toast.success(msg);
      }
    } catch (e) {
      toast.error(e?.response?.data?.detail || "M-Pesa test failed");
    }
  };

  const handleTestLks = async () => {
    try {
      const { data } = await systemApi.testLks();
      if (data.ok) toast.success(data.message || "LKS auth successful", { duration: 6000 });
      else toast.error(data.message || "LKS auth failed", { duration: 8000 });
    } catch (e) {
      toast.error(e?.response?.data?.detail || "LKS test failed");
    }
  };


  const handleTestSms = async () => {
    try {
      const { data } = await systemApi.testSms(testSmsTo);
      toast.success(data.message || "SMS test OK");
    } catch (e) {
      toast.error(e?.response?.data?.detail || "SMS test failed");
    }
  };

  const handleTestWa = async () => {
    try {
      const { data } = await systemApi.testWhatsapp(testWaTo);
      toast.success(data.message || "WhatsApp test OK");
    } catch (e) {
      toast.error(e?.response?.data?.detail || "WhatsApp test failed");
    }
  };

  const handleSetRole = async (userId, role) => {
    try {
      await systemApi.setRole(userId, role);
      toast.success(`Role updated to ${role}`);
      loadAdmins();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Failed to change role");
    }
  };

  if (loading) return <div className="min-h-screen flex items-center justify-center"><div className="h-8 bg-muted rounded w-32 animate-pulse" /></div>;
  if (!config) return null;

  return (
    <div className="min-h-screen bg-background lg:flex" data-testid="system-admin">
      <Sidebar items={TABS} activeKey={tab} onSelect={setTab} accent="slate" title="System" />

      <div className="flex-1 lg:pl-64">
      {/* Mobile header */}
      <header className="sticky top-0 z-40 bg-slate-900 text-white px-4 py-3 flex items-center justify-between lg:hidden">
        <div className="flex items-center gap-2">
          <div className="w-7 h-7 rounded-full bg-white/20 flex items-center justify-center"><ShieldCheck className="w-4 h-4" weight="fill" /></div>
          <span className="font-heading text-lg font-bold tracking-tighter">Utheri</span>
          <span className="text-xs bg-amber-500 text-slate-900 px-2 py-0.5 rounded-lg ml-1 font-semibold">System Admin</span>
        </div>
        <div className="flex items-center gap-2">
          <ViewToggle onDark />
          <button onClick={logout} className="p-1.5 rounded-lg hover:bg-white/10" data-testid="system-logout-btn"><SignOut className="w-4 h-4" /></button>
        </div>
      </header>

      {/* Desktop top bar */}
      <header className="hidden lg:flex sticky top-0 z-40 bg-background/80 backdrop-blur-xl border-b border-border px-8 py-3 items-center justify-between">
        <h1 className="font-heading text-xl font-semibold capitalize">{TABS.find(t => t.key === tab)?.label || "System"}</h1>
        <ViewToggle />
      </header>

      <div className="max-w-4xl mx-auto px-4 lg:px-8 py-6 space-y-5">
        <div>
          <h1 className="font-heading text-2xl font-semibold flex items-center gap-2"><Gear className="w-6 h-6 text-amber-500" /> System Configuration</h1>
          <p className="text-sm text-muted-foreground mt-1">Configure integrations and platform settings. Only Super Admins see this page. Signed in as <span className="font-semibold">{user?.name}</span>.</p>
        </div>

        {/* Tabs (mobile only — desktop uses sidebar) */}
        <div className="flex gap-1 bg-muted p-1 rounded-xl overflow-x-auto lg:hidden">
          {TABS.map((t) => (
            <button key={t.key} onClick={() => setTab(t.key)} data-testid={`system-tab-${t.key}`}
              className={`flex-1 min-w-max flex items-center justify-center gap-1.5 py-2 px-3 rounded-lg text-xs sm:text-sm font-medium transition-colors ${tab === t.key ? "bg-card text-foreground shadow-sm" : "text-muted-foreground hover:text-foreground"}`}>
              <t.icon className="w-4 h-4" />
              <span>{t.label}</span>
            </button>
          ))}
        </div>

        {/* Email */}
        {tab === "email" && (
          <SectionCard title="Email / SMTP" data-testid="email-section"
            onSave={() => handleSaveSection("email")} saving={saving}
            helper="Configure SMTP for admin alerts on new registrations & member auto-activation. For Gmail use an App Password (2FA required).">
            <Field label="SMTP Host" value={config.email.host} onChange={(v) => updateField("email", "host", v)} placeholder="smtp.gmail.com" testid="email-host" />
            <Field label="Port" value={config.email.port} type="number" onChange={(v) => updateField("email", "port", Number(v) || 587)} testid="email-port" />
            <Field label="Username" value={config.email.username} onChange={(v) => updateField("email", "username", v)} placeholder="email@domain.com" testid="email-username" />
            <Field label="Password" value={config.email.password} onChange={(v) => updateField("email", "password", v)} type="password" placeholder="App Password" testid="email-password" sensitive />
            <Field label="From Email" value={config.email.fromEmail} onChange={(v) => updateField("email", "fromEmail", v)} placeholder="noreply@utheri.co.ke" testid="email-from" />
            <Field label="From Name" value={config.email.fromName} onChange={(v) => updateField("email", "fromName", v)} placeholder="Utheri Welfare" testid="email-fromname" />
            <Checkbox label="Use STARTTLS (port 587 default)" checked={config.email.useTls} onChange={(v) => updateField("email", "useTls", v)} testid="email-tls" />
            <Checkbox label="Use SSL (port 465)" checked={config.email.useSsl} onChange={(v) => updateField("email", "useSsl", v)} testid="email-ssl" />
            <Field label="Admin Notification Email" value={config.email.adminEmail} onChange={(v) => updateField("email", "adminEmail", v)} placeholder="admin@utheri.co.ke" testid="email-admin-email" />
            <div className="flex gap-2 pt-3 border-t border-border">
              <input type="email" value={testEmailTo} onChange={(e) => setTestEmailTo(e.target.value)} placeholder="recipient@example.com" className="flex-1 px-3 py-2 text-sm bg-card border border-input rounded-xl" data-testid="email-test-recipient" />
              <button onClick={handleTestEmail} className="px-4 py-2 rounded-xl bg-secondary text-secondary-foreground font-medium text-sm" data-testid="email-test-send-btn">Send test email</button>
            </div>
          </SectionCard>
        )}

        {/* M-Pesa */}
        {tab === "mpesa" && (
          <SectionCard title="M-Pesa Daraja API" data-testid="mpesa-section"
            onSave={() => handleSaveSection("mpesa")} saving={saving}
            helper="Configure Safaricom Daraja for STK Push contributions. Use sandbox for testing, production when live. Callback URL must be publicly reachable.">
            <Checkbox label="Enable M-Pesa" checked={config.mpesa.enabled} onChange={(v) => updateField("mpesa", "enabled", v)} testid="mpesa-enabled" />
            <div>
              <label className="text-sm font-medium block mb-1">Environment</label>
              <select value={config.mpesa.environment} onChange={(e) => updateField("mpesa", "environment", e.target.value)} className="w-full px-3 py-2 text-sm bg-card border border-input rounded-xl" data-testid="mpesa-environment">
                <option value="sandbox">Sandbox (testing)</option>
                <option value="production">Production (live)</option>
              </select>
            </div>
            <Field label="Consumer Key" value={config.mpesa.consumerKey} onChange={(v) => updateField("mpesa", "consumerKey", v)} testid="mpesa-key" />
            <Field label="Consumer Secret" value={config.mpesa.consumerSecret} onChange={(v) => updateField("mpesa", "consumerSecret", v)} type="password" testid="mpesa-secret" sensitive />
            <Field label="Passkey (Lipa Na M-Pesa Online)" value={config.mpesa.passkey} onChange={(v) => updateField("mpesa", "passkey", v)} type="password" testid="mpesa-passkey" sensitive />
            <Field label="Shortcode / Paybill" value={config.mpesa.shortcode} onChange={(v) => updateField("mpesa", "shortcode", v)} placeholder="174379" testid="mpesa-shortcode" />
            <Field label="Callback URL" value={config.mpesa.callbackUrl} onChange={(v) => updateField("mpesa", "callbackUrl", v)} placeholder={`${process.env.REACT_APP_BACKEND_URL}/api/mpesa/callback`} testid="mpesa-callback" />
            <div className="flex items-center justify-between gap-3 pt-1">
              <p className="text-xs text-muted-foreground">
                Tip: paste <code className="font-mono text-[11px] bg-secondary px-1 py-0.5 rounded">{process.env.REACT_APP_BACKEND_URL}/api/mpesa/callback</code> as your Daraja callback. Safaricom requires HTTPS and a publicly reachable URL.
              </p>
              <button
                type="button"
                onClick={() => updateField("mpesa", "callbackUrl", `${process.env.REACT_APP_BACKEND_URL}/api/mpesa/callback`)}
                className="text-xs px-2 py-1 rounded-md bg-secondary text-secondary-foreground border border-border whitespace-nowrap"
                data-testid="mpesa-fill-callback-btn"
              >
                Fill in
              </button>
            </div>
            <div className="pt-3 border-t border-border">
              <button onClick={handleTestMpesa} className="px-4 py-2 rounded-xl bg-secondary text-secondary-foreground font-medium text-sm" data-testid="mpesa-test-btn">Validate config</button>
            </div>
            <Banner>STK Push is fully wired. Validate config above — once it returns "Credentials valid" with <strong>ready: true</strong>, members tapping Pay Now will receive a real Safaricom STK prompt on their phone.</Banner>
            <RailSelector config={config} onChange={async (rail) => {
              try {
                await systemApi.setPaymentRail(rail);
                setConfig(c => ({ ...c, paymentRail: rail }));
                toast.success(`Active payment rail set to ${rail.toUpperCase()}`);
              } catch (e) {
                toast.error(e?.response?.data?.detail || "Could not switch rail");
              }
            }} />
          </SectionCard>
        )}

        {/* LKS (alternative STK rail) */}
        {tab === "lks" && (
          <SectionCard title="LIPA KWA SACCO (LKS) — alternative rail" data-testid="lks-section"
            onSave={() => handleSaveSection("lks")} saving={saving}
            helper="An alternative STK aggregator that some SACCOs prefer over direct Daraja. Configure here, then switch the active rail from the M-Pesa section or below.">
            <Checkbox label="Enable LKS" checked={config.lks?.enabled} onChange={(v) => updateField("lks", "enabled", v)} testid="lks-enabled" />
            <Field label="Base URL" value={config.lks?.baseUrl} onChange={(v) => updateField("lks", "baseUrl", v)} placeholder="https://lks-engine.spotca.sh" testid="lks-baseurl" />
            <Field label="Vendor Username" value={config.lks?.username} onChange={(v) => updateField("lks", "username", v)} testid="lks-username" />
            <Field label="Vendor Password" value={config.lks?.password} onChange={(v) => updateField("lks", "password", v)} type="password" testid="lks-password" sensitive />
            <Field label="Merchant Code" value={config.lks?.merchantCode} onChange={(v) => updateField("lks", "merchantCode", v)} placeholder="(optional)" testid="lks-merchant" />
            <Field label="Client ID" value={config.lks?.clientId} onChange={(v) => updateField("lks", "clientId", v)} placeholder="(optional)" testid="lks-clientid" />
            <Field label="Shared Secret (X-LKS-Token)" value={config.lks?.sharedSecret} onChange={(v) => updateField("lks", "sharedSecret", v)} type="password" testid="lks-secret" sensitive />

            <div className="pt-3 border-t border-border">
              <p className="text-xs font-semibold text-foreground mb-2">Endpoint paths (v1.1)</p>
              <p className="text-[11px] text-muted-foreground mb-2">
                These follow the LKS Vendor API spec — leave as-is unless LKS
                support gives you new paths. Full URL = <span className="font-mono">Base URL + path</span>.
              </p>
              <Field label="Auth path" value={config.lks?.authPath} onChange={(v) => updateField("lks", "authPath", v)} placeholder="/api/vendor/auth/requestToken" testid="lks-auth-path" />
              <Field label="STK Push path" value={config.lks?.stkPath} onChange={(v) => updateField("lks", "stkPath", v)} placeholder="/api/vendor/payment/lksMerchantTransaction" testid="lks-stk-path" />
              <Field label="Status Query path" value={config.lks?.statusPath} onChange={(v) => updateField("lks", "statusPath", v)} placeholder="/api/vendor/payment/status" testid="lks-status-path" />
              <p className="text-[10px] text-muted-foreground mt-1">Status Query appends <span className="font-mono">/&lt;thirdPartyTrxId&gt;</span> automatically.</p>
            </div>
            <div className="flex items-center justify-between gap-3 pt-1">
              <p className="text-xs text-muted-foreground">
                IPN callback URL: <code className="font-mono text-[11px] bg-secondary px-1 py-0.5 rounded">{process.env.REACT_APP_BACKEND_URL}/api/lks/callback</code>. Paste this into your LKS portal. The shared secret above must be set in BOTH places to prevent forged callbacks.
              </p>
              <button
                type="button"
                onClick={() => navigator.clipboard?.writeText(`${process.env.REACT_APP_BACKEND_URL}/api/lks/callback`).then(() => toast.success("Copied"))}
                className="text-xs px-2 py-1 rounded-md bg-secondary text-secondary-foreground border border-border whitespace-nowrap"
                data-testid="lks-copy-callback-btn"
              >
                <Copy className="w-3 h-3 inline mr-1" /> Copy
              </button>
            </div>
            <div className="pt-3 border-t border-border">
              <button onClick={handleTestLks} className="px-4 py-2 rounded-xl bg-secondary text-secondary-foreground font-medium text-sm" data-testid="lks-test-btn">Validate LKS auth</button>
            </div>
            <Banner>
              Multi-tenant ready: each tenant can hold its own LKS account on its own settings doc. For best security, IP-whitelist LKS&apos;s outbound IP at your reverse proxy in addition to the shared secret above.
            </Banner>
            <RailSelector config={config} onChange={async (rail) => {
              try {
                await systemApi.setPaymentRail(rail);
                setConfig(c => ({ ...c, paymentRail: rail }));
                toast.success(`Active payment rail set to ${rail.toUpperCase()}`);
              } catch (e) {
                toast.error(e?.response?.data?.detail || "Could not switch rail");
              }
            }} />
          </SectionCard>
        )}

        {/* SMS */}
        {tab === "sms" && (
          <SectionCard title="SMS Provider" data-testid="sms-section"
            onSave={() => handleSaveSection("sms")} saving={saving}
            helper="Choose between Africa's Talking (recommended for Kenya) or Twilio. Required fields differ per provider.">
            <Checkbox label="Enable SMS" checked={config.sms.enabled} onChange={(v) => updateField("sms", "enabled", v)} testid="sms-enabled" />
            <div>
              <label className="text-sm font-medium block mb-1">Provider</label>
              <select value={config.sms.provider} onChange={(e) => updateField("sms", "provider", e.target.value)} className="w-full px-3 py-2 text-sm bg-card border border-input rounded-xl" data-testid="sms-provider">
                <option value="africastalking">Africa's Talking</option>
                <option value="twilio">Twilio</option>
              </select>
            </div>
            {config.sms.provider === "africastalking" ? (
              <>
                <Field label="Username" value={config.sms.username} onChange={(v) => updateField("sms", "username", v)} placeholder="sandbox (for test) or your username" testid="sms-at-username" />
                <Field label="API Key" value={config.sms.apiKey} onChange={(v) => updateField("sms", "apiKey", v)} type="password" testid="sms-at-key" sensitive />
                <Field label="Sender ID (Short Code)" value={config.sms.senderId} onChange={(v) => updateField("sms", "senderId", v)} placeholder="UTHERI" testid="sms-at-sender" />
              </>
            ) : (
              <>
                <Field label="Account SID" value={config.sms.accountSid} onChange={(v) => updateField("sms", "accountSid", v)} testid="sms-twilio-sid" />
                <Field label="Auth Token" value={config.sms.authToken} onChange={(v) => updateField("sms", "authToken", v)} type="password" testid="sms-twilio-token" sensitive />
                <Field label="From Number" value={config.sms.fromNumber} onChange={(v) => updateField("sms", "fromNumber", v)} placeholder="+1415..." testid="sms-twilio-from" />
              </>
            )}
            <div className="flex gap-2 pt-3 border-t border-border">
              <input type="tel" value={testSmsTo} onChange={(e) => setTestSmsTo(e.target.value)} placeholder="0712345678" className="flex-1 px-3 py-2 text-sm bg-card border border-input rounded-xl" data-testid="sms-test-recipient" />
              <button onClick={handleTestSms} className="px-4 py-2 rounded-xl bg-secondary text-secondary-foreground font-medium text-sm" data-testid="sms-test-send-btn">Validate config</button>
            </div>
            <Banner>SMS sending is fully wired (Africa's Talking + Twilio). Validate above to send a real test message — costs ≈ KES 0.80 per SMS. Once configured, members logging in from a new browser will receive a 6-digit code to verify it's them.</Banner>
          </SectionCard>
        )}

        {/* WhatsApp */}
        {tab === "whatsapp" && (
          <SectionCard title="WhatsApp (Meta Cloud API)" data-testid="whatsapp-section"
            onSave={() => handleSaveSection("whatsapp")} saving={saving}
            helper="Use Meta's WhatsApp Cloud API — free tier available. Requires a verified business account and phone number.">
            <Checkbox label="Enable WhatsApp" checked={config.whatsapp.enabled} onChange={(v) => updateField("whatsapp", "enabled", v)} testid="wa-enabled" />
            <Field label="Access Token" value={config.whatsapp.accessToken} onChange={(v) => updateField("whatsapp", "accessToken", v)} type="password" testid="wa-token" sensitive />
            <Field label="Phone Number ID" value={config.whatsapp.phoneNumberId} onChange={(v) => updateField("whatsapp", "phoneNumberId", v)} testid="wa-phone-id" />
            <Field label="App Secret" value={config.whatsapp.appSecret} onChange={(v) => updateField("whatsapp", "appSecret", v)} type="password" testid="wa-secret" sensitive />
            <Field label="Verify Token (for webhooks)" value={config.whatsapp.verifyToken} onChange={(v) => updateField("whatsapp", "verifyToken", v)} testid="wa-verify" />
            <div className="flex gap-2 pt-3 border-t border-border">
              <input type="tel" value={testWaTo} onChange={(e) => setTestWaTo(e.target.value)} placeholder="+254712345678" className="flex-1 px-3 py-2 text-sm bg-card border border-input rounded-xl" data-testid="wa-test-recipient" />
              <button onClick={handleTestWa} className="px-4 py-2 rounded-xl bg-secondary text-secondary-foreground font-medium text-sm" data-testid="wa-test-send-btn">Validate config</button>
            </div>
            <Banner>WhatsApp send backend is scaffolded. Once credentials are saved, the backend is ready to be wired (next release).</Banner>
          </SectionCard>
        )}

        {/* Branding */}
        {tab === "branding" && (
          <SectionCard title="Branding & UI" data-testid="branding-section"
            onSave={() => handleSaveSection("branding")} saving={saving}
            helper="Customize the app name, logo and colours shown to members.">
            <Field label="App Name" value={config.branding.appName} onChange={(v) => updateField("branding", "appName", v)} testid="branding-appname" />
            <Field label="Tagline" value={config.branding.tagline} onChange={(v) => updateField("branding", "tagline", v)} testid="branding-tagline" />
            <Field label="Logo URL" value={config.branding.logoUrl} onChange={(v) => updateField("branding", "logoUrl", v)} placeholder="https://..." testid="branding-logo" />
            <Field label="Primary Color (hex)" value={config.branding.primaryColor} onChange={(v) => updateField("branding", "primaryColor", v)} placeholder="#0f766e" testid="branding-color" />
            <Field label="Footer Text" value={config.branding.footerText} onChange={(v) => updateField("branding", "footerText", v)} testid="branding-footer" />
            <Field label="Support Email" value={config.branding.supportEmail} onChange={(v) => updateField("branding", "supportEmail", v)} testid="branding-support-email" />
            <Field label="Support Phone" value={config.branding.supportPhone} onChange={(v) => updateField("branding", "supportPhone", v)} testid="branding-support-phone" />
          </SectionCard>
        )}

        {/* Admin Users */}
        {tab === "admins" && (
          <div className="bg-card border border-border rounded-xl p-6 space-y-4" data-testid="admins-section">
            <div>
              <h2 className="font-heading text-lg font-semibold">Admin Users & Roles</h2>
              <p className="text-xs text-muted-foreground mt-1">Super Admins have full access (including this System page). Welfare Admins manage members, contributions, obituaries. Demote to Member to revoke admin access.</p>
            </div>
            <div className="divide-y divide-border rounded-xl border border-border overflow-hidden">
              {admins.length === 0 ? <p className="p-6 text-center text-sm text-muted-foreground">No admins found.</p> : admins.map((a) => (
                <div key={a.id} className="flex items-center justify-between gap-3 px-4 py-3" data-testid={`admin-row-${a.id}`}>
                  <div>
                    <p className="font-semibold text-sm">{a.name}</p>
                    <p className="text-xs text-muted-foreground">{a.phone} · {a.ukcNumber || a.memberNumber || "—"}</p>
                  </div>
                  <div className="flex items-center gap-2">
                    <span className={`text-xs px-2 py-0.5 rounded-full font-medium ${
                      a.role === "super_admin" ? "bg-amber-100 text-amber-900" :
                      a.role === "admin" ? "bg-blue-100 text-blue-800" :
                      a.role === "treasurer" ? "bg-emerald-100 text-emerald-800" :
                      a.role === "secretary" ? "bg-indigo-100 text-indigo-800" :
                      "bg-slate-100 text-slate-700"
                    }`}>
                      {a.role === "super_admin" ? "Super Admin" : a.role === "admin" ? "Welfare Admin" : a.role.charAt(0).toUpperCase() + a.role.slice(1)}
                    </span>
                    <select value={a.role} onChange={(e) => handleSetRole(a.id, e.target.value)} className="px-2 py-1 text-xs bg-card border border-input rounded-lg" data-testid={`admin-role-${a.id}`}>
                      <option value="super_admin">Super Admin</option>
                      <option value="admin">Welfare Admin</option>
                      <option value="treasurer">Treasurer</option>
                      <option value="secretary">Secretary</option>
                      <option value="member">Demote to Member</option>
                    </select>
                  </div>
                </div>
              ))}
            </div>
            <p className="text-xs text-muted-foreground">To promote a regular member to admin, open <Link to="/admin" className="text-primary underline">Welfare Admin → All Members</Link>, find the member, then return here (role column appears once they're active).</p>
          </div>
        )}

        {/* Login Support — admin-driven OTP recovery */}
        {tab === "loginsupport" && (
          <LoginSupportTab />
        )}

        {/* Export Bundle — full DB + code + .env download for off-platform deploys */}
        {tab === "export" && (
          <ExportBundleTab />
        )}

        {/* Notifications report — full audit including deleted */}
        {tab === "notifications" && (
          <NotificationsReportTab />
        )}
      </div>
      </div>
    </div>
  );
}

// ─── Subcomponents ───────────────────────────────────────────────────────────

function SectionCard({ title, helper, children, onSave, saving, ...rest }) {
  return (
    <div className="bg-card border border-border rounded-xl p-6 space-y-4" {...rest}>
      <div>
        <h2 className="font-heading text-lg font-semibold">{title}</h2>
        {helper && <p className="text-xs text-muted-foreground mt-1">{helper}</p>}
      </div>
      <div className="space-y-3">{children}</div>
      <div className="flex gap-2 pt-3 border-t border-border">
        <button onClick={onSave} disabled={saving} className="px-4 py-2 rounded-xl bg-primary text-primary-foreground font-medium text-sm active:scale-95 disabled:opacity-60" data-testid={`${rest["data-testid"]}-save-btn`}>
          {saving ? "Saving..." : "Save"}
        </button>
      </div>
    </div>
  );
}

function Field({ label, value, onChange, type = "text", placeholder, testid, sensitive }) {
  const isMasked = sensitive && typeof value === "string" && value.startsWith("****");
  const handleFocus = (e) => {
    // When a sensitive field shows the server-side mask (e.g. "****abcd"),
    // clear it on focus so the next paste/type does NOT get prefixed with the
    // mask (which the backend would then ignore as an unchanged value).
    if (isMasked) {
      onChange("");
      try { e.target.select(); } catch (_) { /* selection optional */ void 0; }
    }
  };
  return (
    <div>
      <label className="text-sm font-medium block mb-1">{label}</label>
      <input
        type={type}
        value={value ?? ""}
        onChange={(e) => onChange(e.target.value)}
        onFocus={handleFocus}
        placeholder={placeholder || (isMasked ? "(stored — click to replace)" : "")}
        className="w-full px-3 py-2 text-sm bg-card border border-input rounded-xl font-mono"
        data-testid={testid}
      />
      {sensitive && <p className="text-xs text-muted-foreground mt-1">Sensitive — masked after save. Click the field to re-enter.</p>}
    </div>
  );
}

function Checkbox({ label, checked, onChange, testid }) {
  return (
    <label className="flex items-center gap-2 cursor-pointer">
      <input type="checkbox" checked={!!checked} onChange={(e) => onChange(e.target.checked)} className="w-4 h-4 rounded border-input" data-testid={testid} />
      <span className="text-sm">{label}</span>
    </label>
  );
}

function Banner({ children }) {
  return (
    <div className="bg-amber-50 border border-amber-200 rounded-xl p-3 text-xs text-amber-900">
      {children}
    </div>
  );
}

// ─── Login Support Tab ────────────────────────────────────────────────────────

function LoginSupportTab() {
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [revealedCode, setRevealedCode] = useState(null); // {pendingId, code, member, expiresAt}
  const [revealCountdown, setRevealCountdown] = useState(0);
  const [busyId, setBusyId] = useState(null);
  const intervalRef = useRef(null);
  const countdownRef = useRef(null);

  const load = useCallback(async () => {
    try {
      const { data } = await systemApi.listPendingOtps();
      setItems(data || []);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Failed to load pending logins");
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    load();
    intervalRef.current = setInterval(load, 30000); // refresh every 30s
    return () => {
      if (intervalRef.current) clearInterval(intervalRef.current);
      if (countdownRef.current) clearInterval(countdownRef.current);
    };
  }, [load]);

  // Countdown for the revealed code's lifetime
  useEffect(() => {
    if (!revealedCode) {
      if (countdownRef.current) { clearInterval(countdownRef.current); countdownRef.current = null; }
      return;
    }
    const tick = () => {
      const ms = new Date(revealedCode.expiresAt).getTime() - Date.now();
      const s = Math.max(0, Math.floor(ms / 1000));
      setRevealCountdown(s);
      if (s === 0) setRevealedCode(null);
    };
    tick();
    countdownRef.current = setInterval(tick, 1000);
    return () => { if (countdownRef.current) clearInterval(countdownRef.current); };
  }, [revealedCode]);

  const handleRegenerate = async (item) => {
    setBusyId(item.pendingId);
    try {
      const { data } = await systemApi.regenerateOtp(item.pendingId);
      setRevealedCode({
        pendingId: item.pendingId,
        code: data.code,
        member: { name: item.memberName, ukc: item.memberUkc, phone: item.memberPhone },
        expiresAt: new Date(Date.now() + (data.expiresInSeconds * 1000)).toISOString(),
      });
      toast.success("Recovery code generated. Read it to the member now.");
      load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Failed to generate code");
    } finally {
      setBusyId(null);
    }
  };

  const copyCode = () => {
    if (!revealedCode?.code) return;
    navigator.clipboard.writeText(revealedCode.code);
    toast.success("Copied");
  };

  const fmtAge = (s) => {
    if (s < 60) return `${s}s ago`;
    if (s < 3600) return `${Math.floor(s / 60)}m ago`;
    return `${Math.floor(s / 3600)}h ago`;
  };

  return (
    <div className="bg-card border border-border rounded-xl p-6 space-y-4" data-testid="login-support-section">
      <div className="flex items-start justify-between gap-3 flex-wrap">
        <div>
          <h2 className="font-heading text-lg font-semibold flex items-center gap-2">
            <Lifebuoy className="w-5 h-5 text-primary" weight="duotone" /> Login Support
          </h2>
          <p className="text-xs text-muted-foreground mt-1 max-w-prose">
            Members who are stuck on the OTP screen appear here. If their SMS or email never arrived, click <strong>Generate code</strong> to mint a fresh 6-digit recovery code and read it to them over the phone. The code is shown once and expires in 10 minutes.
          </p>
        </div>
        <button
          onClick={load}
          className="inline-flex items-center gap-1.5 text-xs px-3 py-2 rounded-xl bg-secondary text-secondary-foreground border border-border font-medium active:scale-95"
          data-testid="login-support-refresh-btn"
        >
          <ArrowsClockwise className="w-4 h-4" weight="bold" /> Refresh
        </button>
      </div>

      {/* Revealed-code banner */}
      {revealedCode && (
        <div className="bg-primary/5 border-2 border-primary rounded-xl p-4 space-y-3" data-testid="revealed-code-banner">
          <div className="flex items-center justify-between gap-2">
            <p className="text-xs font-medium text-foreground">
              Reading to <strong>{revealedCode.member.name}</strong> ({revealedCode.member.ukc} · {revealedCode.member.phone})
            </p>
            <button
              type="button"
              onClick={() => setRevealedCode(null)}
              className="text-xs text-muted-foreground hover:text-foreground"
              data-testid="dismiss-revealed-code-btn"
            >
              I've read it — hide
            </button>
          </div>
          <div className="flex items-center gap-3">
            <div className="font-mono text-4xl font-bold tracking-[0.3em] text-primary bg-primary/10 px-6 py-3 rounded-xl flex-1 text-center" data-testid="revealed-code-value">
              {revealedCode.code}
            </div>
            <button
              type="button"
              onClick={copyCode}
              className="inline-flex flex-col items-center gap-0.5 px-3 py-3 rounded-xl bg-secondary text-secondary-foreground border border-border hover:bg-secondary/70 active:scale-95"
              data-testid="copy-revealed-code-btn"
              title="Copy to clipboard"
            >
              <Copy className="w-4 h-4" weight="bold" />
              <span className="text-[10px]">Copy</span>
            </button>
          </div>
          <p className="text-xs text-muted-foreground">
            Expires in <strong className="font-mono text-foreground">{Math.floor(revealCountdown / 60)}:{String(revealCountdown % 60).padStart(2, "0")}</strong>. Code is shown once — refresh and regenerate if you lose this view.
          </p>
        </div>
      )}

      {/* Pending list */}
      {loading ? (
        <div className="space-y-2">{[1, 2].map(i => <div key={i} className="h-14 bg-muted rounded-xl animate-pulse" />)}</div>
      ) : items.length === 0 ? (
        <div className="text-center py-10 text-sm text-muted-foreground" data-testid="no-pending-otps">
          <ShieldCheck className="w-10 h-10 mx-auto mb-2 text-muted-foreground/40" weight="duotone" />
          No members are stuck on OTP right now.
        </div>
      ) : (
        <div className="divide-y divide-border rounded-xl border border-border overflow-hidden">
          {items.map((it) => (
            <div key={it.pendingId} className="flex items-center justify-between gap-3 px-4 py-3" data-testid={`pending-otp-row-${it.pendingId}`}>
              <div className="flex-1 min-w-0">
                <p className="font-semibold text-sm truncate">{it.memberName}</p>
                <p className="text-xs text-muted-foreground">
                  {it.memberPhone} · {it.memberUkc || "—"} · sent via {it.channel?.toUpperCase()} to {it.contactMasked} · {fmtAge(it.ageSeconds)}
                </p>
                <p className="text-xs text-muted-foreground/80 mt-0.5">
                  {it.attemptsRemaining} attempt(s) remaining · resends: {it.resendCount}
                </p>
              </div>
              <button
                onClick={() => handleRegenerate(it)}
                disabled={busyId === it.pendingId}
                className="px-3 py-2 rounded-xl bg-primary text-primary-foreground text-xs font-semibold active:scale-95 disabled:opacity-60 shrink-0"
                data-testid={`regenerate-otp-btn-${it.pendingId}`}
              >
                {busyId === it.pendingId ? "Generating…" : "Generate code"}
              </button>
            </div>
          ))}
        </div>
      )}

      <p className="text-xs text-muted-foreground border-t border-border pt-3">
        <strong>Privacy note:</strong> Original OTP codes are stored hashed and cannot be retrieved. This panel mints a brand-new code that replaces the previous one. Regeneration is logged with your admin id.
      </p>
    </div>
  );
}


// ─── Export Bundle ───────────────────────────────────────────────────────────
function ExportBundleTab() {
  const [building, setBuilding] = useState(false);
  const [downloading, setDownloading] = useState(false);
  const [info, setInfo] = useState(null);

  const buildBundle = async () => {
    setBuilding(true);
    try {
      const { data } = await systemApi.buildExportBundle();
      setInfo(data);
      toast.success(`Bundle ready — ${(data.sizeBytes / 1_048_576).toFixed(1)} MB`);
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Failed to build bundle");
    } finally {
      setBuilding(false);
    }
  };

  const downloadBundle = async () => {
    setDownloading(true);
    try {
      const res = await systemApi.downloadExportBundle();
      const blob = new Blob([res.data], { type: "application/zip" });
      const url = URL.createObjectURL(blob);
      const ts = new Date().toISOString().replace(/[:.]/g, "-").slice(0, 19);
      const a = document.createElement("a");
      a.href = url;
      a.download = `utheri-bundle-${ts}.zip`;
      document.body.appendChild(a);
      a.click();
      a.remove();
      URL.revokeObjectURL(url);
      toast.success("Download started");
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Download failed");
    } finally {
      setDownloading(false);
    }
  };

  return (
    <div className="bg-card border border-border rounded-xl p-6 space-y-5" data-testid="export-bundle-section">
      <div className="flex items-start gap-3">
        <div className="w-10 h-10 rounded-xl bg-primary/10 flex items-center justify-center shrink-0">
          <Archive className="w-5 h-5 text-primary" weight="duotone" />
        </div>
        <div>
          <h2 className="font-heading text-lg font-semibold">Production export bundle</h2>
          <p className="text-xs text-muted-foreground mt-1 leading-relaxed">
            Generates a single zip containing a fresh MongoDB dump, the full backend &amp; frontend source,
            and both <code className="px-1 rounded bg-secondary text-foreground">.env</code> files. Use this to redeploy
            on your own VPS, Render, Railway, Fly.io, or any host outside Emergent.
          </p>
        </div>
      </div>

      <div className="bg-amber-50 border border-amber-200 text-amber-900 text-xs rounded-xl p-3 leading-relaxed">
        <strong>Treat this file as confidential.</strong> It contains the JWT secret, M-Pesa Daraja keys,
        SMS/SMTP credentials, and every member's hashed password. Rotate every secret in
        <code className="mx-1 px-1 rounded bg-amber-100">backend/.env</code> immediately after restoring on a production host.
      </div>

      <div className="flex flex-col sm:flex-row gap-2">
        <button
          onClick={buildBundle}
          disabled={building || downloading}
          data-testid="build-bundle-btn"
          className="inline-flex items-center justify-center gap-2 px-4 py-2.5 rounded-xl bg-secondary text-foreground border border-border font-medium text-sm active:scale-95 disabled:opacity-60"
        >
          <ArrowsClockwise className={`w-4 h-4 ${building ? "animate-spin" : ""}`} weight="bold" />
          {building ? "Building…" : "Rebuild bundle"}
        </button>
        <button
          onClick={downloadBundle}
          disabled={building || downloading}
          data-testid="download-bundle-btn"
          className="inline-flex items-center justify-center gap-2 px-5 py-2.5 rounded-xl bg-primary text-primary-foreground font-semibold text-sm active:scale-95 disabled:opacity-60 flex-1"
        >
          <DownloadSimple className="w-4 h-4" weight="fill" />
          {downloading ? "Preparing download…" : "Download latest bundle"}
        </button>
      </div>

      {info && (
        <div className="text-xs text-muted-foreground border-t border-border pt-3 space-y-1" data-testid="bundle-info">
          <p><strong>Path:</strong> <code className="font-mono">{info.path}</code></p>
          <p><strong>Size:</strong> {(info.sizeBytes / 1_048_576).toFixed(2)} MB</p>
          {info.log && (
            <pre className="font-mono text-[10px] bg-secondary p-2 rounded mt-2 whitespace-pre-wrap">{info.log.join("\n")}</pre>
          )}
        </div>
      )}

      <div className="text-xs text-muted-foreground border-t border-border pt-3 space-y-2 leading-relaxed">
        <p className="font-semibold text-foreground">What's inside the zip</p>
        <ul className="list-disc list-inside space-y-0.5">
          <li><code>db_dump/</code> — BSON dump of every collection (members, contributions, payouts, settings)</li>
          <li><code>backend/</code> — FastAPI source + <code>.env</code> + <code>requirements.txt</code> + tests</li>
          <li><code>frontend/</code> — React source + <code>.env</code> + <code>package.json</code> + <code>yarn.lock</code></li>
          <li><code>memory/</code> — PRD, changelog, test credentials</li>
          <li><code>README.md</code> — restore + deploy step-by-step instructions</li>
        </ul>
      </div>
    </div>
  );
}

// ─── Notifications report ────────────────────────────────────────────────────
function NotificationsReportTab() {
  const [data, setData] = useState(null);
  const [refreshing, setRefreshing] = useState(false);
  const [filter, setFilter] = useState("all"); // all | live | deleted

  const fetchReport = async () => {
    setRefreshing(true);
    try {
      const { data } = await notificationsApi.report(true);
      setData(data);
    } catch (_) {
      setData({ rows: [], totals: {} });
    } finally {
      setRefreshing(false);
    }
  };

  useEffect(() => { fetchReport(); }, []);

  const totals = data?.totals || {};
  const rows = (data?.rows || []).filter((r) => {
    if (filter === "live") return r._state === "live";
    if (filter === "deleted") return r._state === "deleted";
    return true;
  });

  return (
    <div className="bg-card border border-border rounded-xl p-6 space-y-4" data-testid="notifications-report-tab">
      <div className="flex items-start gap-3">
        <div className="w-10 h-10 rounded-xl bg-primary/10 flex items-center justify-center shrink-0">
          <Bell className="w-5 h-5 text-primary" weight="duotone" />
        </div>
        <div className="flex-1">
          <h2 className="font-heading text-lg font-semibold">Notifications report</h2>
          <p className="text-xs text-muted-foreground mt-1 leading-relaxed">
            Full audit of every notification ever issued — including ones members or admins have cleared from their inbox.
            Use this to investigate disputes; the live notifications list is intentionally short to keep members focused.
          </p>
        </div>
        <button
          onClick={fetchReport}
          disabled={refreshing}
          data-testid="notifications-report-refresh"
          className="inline-flex items-center gap-1.5 px-3 py-2 rounded-xl bg-secondary text-foreground border border-border text-xs font-semibold active:scale-95 disabled:opacity-60"
        >
          <ArrowsClockwise className={`w-3.5 h-3.5 ${refreshing ? "animate-spin" : ""}`} weight="bold" />
          Refresh
        </button>
      </div>

      <div className="grid grid-cols-3 gap-2">
        <div className="bg-secondary/40 rounded-xl p-3 text-center">
          <p className="text-lg font-bold">{totals.all || 0}</p>
          <p className="text-[10px] text-muted-foreground mt-0.5">All notifications</p>
        </div>
        <div className="bg-emerald-50 border border-emerald-200 rounded-xl p-3 text-center">
          <p className="text-lg font-bold text-emerald-700">{totals.live || 0}</p>
          <p className="text-[10px] text-emerald-900/70 mt-0.5">Currently live</p>
        </div>
        <div className="bg-amber-50 border border-amber-200 rounded-xl p-3 text-center">
          <p className="text-lg font-bold text-amber-700">{totals.archived || 0}</p>
          <p className="text-[10px] text-amber-900/70 mt-0.5">Cleared (archived)</p>
        </div>
      </div>

      <div className="flex gap-2">
        {[
          { v: "all", l: "All" },
          { v: "live", l: "Live only" },
          { v: "deleted", l: "Cleared only" },
        ].map((o) => (
          <button
            key={o.v}
            onClick={() => setFilter(o.v)}
            data-testid={`notifications-filter-${o.v}`}
            className={`px-3 py-1.5 rounded-lg text-xs font-semibold border ${filter === o.v ? "bg-primary text-primary-foreground border-primary" : "bg-card border-border"}`}
          >
            {o.l}
          </button>
        ))}
      </div>

      <div className="border border-border rounded-xl divide-y divide-border max-h-[60vh] overflow-y-auto">
        {data === null ? (
          <p className="text-center py-8 text-sm text-muted-foreground">Loading…</p>
        ) : rows.length === 0 ? (
          <p className="text-center py-8 text-sm text-muted-foreground">No notifications match this filter.</p>
        ) : (
          rows.map((n) => (
            <div key={n.id} className="px-4 py-3 flex items-start gap-3" data-testid={`notification-report-row-${n.id}`}>
              <div className={`w-8 h-8 rounded-lg flex items-center justify-center shrink-0 ${n._state === "deleted" ? "bg-amber-50 text-amber-700" : "bg-primary/10 text-primary"}`}>
                {n._state === "deleted" ? <Trash className="w-4 h-4" /> : <Bell className="w-4 h-4" weight="fill" />}
              </div>
              <div className="flex-1 min-w-0">
                <p className="text-sm leading-snug">{n.message}</p>
                <p className="text-[10px] text-muted-foreground mt-0.5 flex flex-wrap gap-x-2 gap-y-0.5">
                  {n.createdAt && <span>{new Date(n.createdAt).toLocaleString()}</span>}
                  {n.targetUserId && <span>· user {n.targetUserId.slice(-6)}</span>}
                  {n.type && <span>· {n.type}</span>}
                  {n._state === "deleted" && <span className="text-amber-700 font-semibold">· cleared</span>}
                  {n.deletedBy && <span>by {n.deletedBy.slice(-6)}</span>}
                </p>
              </div>
            </div>
          ))
        )}
      </div>
    </div>
  );
}



// ─── Payment Rail Selector ────────────────────────────────────────────────
// Renders the currently active STK rail and lets the super admin flip
// between M-Pesa Daraja and LKS. Surfaced in both the M-Pesa and LKS
// section cards so the toggle is one click away from either set of
// credentials.
function RailSelector({ config, onChange }) {
  const rail = config?.paymentRail || "mpesa";
  return (
    <div className="mt-3 pt-3 border-t border-border space-y-2" data-testid="payment-rail-selector">
      <p className="text-sm font-semibold">Active payment rail</p>
      <p className="text-xs text-muted-foreground">Which provider handles all STK pushes (wallet top-ups, single contributions, bulk arrears). Switch any time — in-flight payments under the old rail will still confirm via their original callback.</p>
      <div className="grid grid-cols-2 gap-2 mt-1">
        <button
          type="button"
          onClick={() => onChange("mpesa")}
          data-testid="rail-mpesa-btn"
          className={`px-3 py-2.5 rounded-xl text-sm font-semibold border-2 transition-all ${rail === "mpesa" ? "border-emerald-600 bg-emerald-50 text-emerald-700" : "border-border bg-card text-muted-foreground hover:bg-secondary"}`}
        >
          M-Pesa Daraja {rail === "mpesa" && <span className="text-[10px] ml-1 align-middle">● ACTIVE</span>}
        </button>
        <button
          type="button"
          onClick={() => onChange("lks")}
          data-testid="rail-lks-btn"
          className={`px-3 py-2.5 rounded-xl text-sm font-semibold border-2 transition-all ${rail === "lks" ? "border-emerald-600 bg-emerald-50 text-emerald-700" : "border-border bg-card text-muted-foreground hover:bg-secondary"}`}
        >
          LKS (alt rail) {rail === "lks" && <span className="text-[10px] ml-1 align-middle">● ACTIVE</span>}
        </button>
      </div>
    </div>
  );
}
